import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { useHasVerifiedPhone } from './use-phone-verification-status';
import { saveUserSetting } from '../store/user/user.actions';
import { UserSettings } from '../enums/user-settings/user-settings';
import { PHONE_VERIFICATION_STATES } from '../authentication/constants';

/**
 * Hook to manage MFA validation state transitions
 * Checks the current state and transitions to the next state if needed:
 * - If 'banner_dismissed' → set to 'modal'
 * - If 'modal_dismissed' → set to 'unskippable'
 *
 * This hook only runs once after login, not on every state change
 */
export const useTransitionMFAValidationSteps = () => {
  const dispatch = useDispatch();
  const { verifiedPhoneState } = useHasVerifiedPhone();
  const hasRunRef = useRef(false);

  useEffect(() => {
    if (hasRunRef.current) {
      return;
    }

    hasRunRef.current = true;

    const terminalStates = [PHONE_VERIFICATION_STATES.VERIFIED, PHONE_VERIFICATION_STATES.BYPASSED_VIA_OTP];
    if (!terminalStates.includes(verifiedPhoneState)) {
      const currentDate = new Date().toISOString().split('T')[0];
      let newState = verifiedPhoneState;

      if (verifiedPhoneState === PHONE_VERIFICATION_STATES.START) {
        newState = PHONE_VERIFICATION_STATES.BANNER;
      } else if (
        verifiedPhoneState === PHONE_VERIFICATION_STATES.BANNER ||
        verifiedPhoneState === PHONE_VERIFICATION_STATES.BANNER_DISMISSED
      ) {
        newState = PHONE_VERIFICATION_STATES.MODAL;
      } else if (
        verifiedPhoneState === PHONE_VERIFICATION_STATES.MODAL ||
        verifiedPhoneState === PHONE_VERIFICATION_STATES.MODAL_DISMISSED
      ) {
        newState = PHONE_VERIFICATION_STATES.UNSKIPPABLE;
      }

      if (newState !== verifiedPhoneState) {
        dispatch(
          saveUserSetting({
            settingName: UserSettings.userHasVerifiedPhoneNumber.settingName,
            settingValue: `${newState}-${currentDate}`,
            excludeV3prefix: true,
          }),
        );
      }
    }
  }, [dispatch, verifiedPhoneState]);
};
